import moment from 'moment';

/** Org default when API omits recorded_timezone (see TRIAL_RECORDING_DATETIME_API.md). */
export const DEFAULT_RECORDED_TIMEZONE = 'America/Los_Angeles';

function normalizeIsoLikeString(s) {
  // Handle Python/DB-style timestamptz strings like:
  // "2026-03-03 23:47:23.158000+00:00" or "2026-03-03 23:47:23+00:00"
  // Normalize to ISO-8601 with 'T' and milliseconds (Moment is picky about 6-digit fractions).
  const m = s.match(
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?([zZ]|[+-]\d{2}:?\d{2})$/
  );
  if (!m) return s;

  const [, date, time, frac = '', tz] = m;
  const ms = frac ? frac.padEnd(3, '0').slice(0, 3) : null;
  return `${date}T${time}${ms ? `.${ms}` : ''}${tz}`;
}

function normalizeDateInput(value) {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const s = String(value).trim();
  if (!s) return null;

  // API may tag update-trial local wall clock with "+00:00"; strip before ISO normalize.
  if (isLocalWallClockWithUtcOffset(s)) {
    return s.replace(/\+00:00$/, '');
  }

  // Accept epoch seconds/milliseconds in string form
  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n)) return s.length === 10 ? n * 1000 : n;
  }

  // Legacy trial names encode local wall time as "YYYY-MM-DD--HH-MM-SS" (use T, not space).
  if (s.includes('--') && !s.includes('T')) {
    const [d, t] = s.split('--');
    if (d && t) return `${d}T${t.replace(/-/g, ':')}`;
  }

  return normalizeIsoLikeString(s);
}

/**
 * update-trial sends "YYYY-MM-DD HH:mm:ss.ffffff" (local wall clock). Some API
 * responses append "+00:00" to that same local value; converting to local TZ
 * would shift it (e.g. 14:51 → 07:51 in PDT). Real UTC uses "T" ISO strings.
 */
function isLocalWallClockWithUtcOffset(s) {
  // update-trial always includes microsecond fraction; real UTC may omit it.
  return (
    typeof s === 'string' &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d+\+00:00$/.test(s)
  );
}

function hasTimezoneInfo(s) {
  // Matches ISO 'Z' or numeric offsets like +05:00 / -0500 at end of string
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
}

function parseToMoment(value) {
  const s = normalizeDateInput(value);
  if (!s) return null;
  const m =
    typeof s === 'number' || s instanceof Date
      ? moment(s)
      : hasTimezoneInfo(s)
        ? moment.parseZone(s)
        : moment(s);
  return m.isValid() ? m : null;
}

function isNaivePostgresDatetime(s) {
  return (
    typeof s === 'string' &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s.trim())
  );
}

/** Naive space-separated value from timestamptz serialization (UTC wall clock). */
function parseNaiveUtcInstant(s) {
  const trimmed = s.trim();
  const formats = [
    'YYYY-MM-DD HH:mm:ss.SSSSSS',
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
  ];
  for (const fmt of formats) {
    const m = moment.utc(trimmed, fmt, true);
    if (m.isValid()) return m.toDate();
  }
  return null;
}

/** Naive update-trial wall clock interpreted in a specific IANA zone. */
function parseNaiveWallInZone(s, timeZone) {
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
  /* istanbul ignore next — only called when isNaivePostgresDatetime already matched */
  if (!m) return null;
  try {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const h = Number(m[4]);
    const mi = Number(m[5]);
    const se = Number(m[6]);
    const base = new Date(Date.UTC(y, mo - 1, d, h, mi, se));
    const utcDate = new Date(base.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(base.toLocaleString('en-US', { timeZone }));
    const offset = utcDate.getTime() - tzDate.getTime();
    return new Date(base.getTime() + offset);
  } catch (_) {
    return null;
  }
}

function formatIntlInZone(instant, timeZone, { withSeconds = true } = {}) {
  const opts = {
    timeZone,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  };
  if (withSeconds) opts.second = '2-digit';
  const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(instant);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  const date = `${get('month')}/${get('day')}/${get('year')}`;
  const time = withSeconds
    ? `${get('hour')}:${get('minute')}:${get('second')}`
    : `${get('hour')}:${get('minute')}`;
  const tz = get('timeZoneName');
  return tz ? `${date} ${time} ${tz}` : `${date} ${time}`;
}

/**
 * Trial recording time: wall clock at recording site + zone label.
 * Uses recorded_timezone when present; otherwise DEFAULT_RECORDED_TIMEZONE.
 * Naive "YYYY-MM-DD HH:mm:ss[.ffffff]" without IANA is treated as UTC wall clock
 * (common timestamptz serialization regression); with IANA it is local wall at the site.
 */
export function formatRecordingDateTime(value, recordedTimezone, { withSeconds = true } = {}) {
  if (value == null) return '';
  const explicitTz = recordedTimezone != null && String(recordedTimezone).trim() !== '';
  const tz = explicitTz ? String(recordedTimezone).trim() : DEFAULT_RECORDED_TIMEZONE;
  const intlOpts = withSeconds ? undefined : { withSeconds: false };

  if (typeof value === 'string' && isLocalWallClockWithUtcOffset(value.trim())) {
    return formatLocalDateTime(value, { withSeconds });
  }

  const rawStr = typeof value === 'string' ? value.trim() : null;
  if (rawStr && isNaivePostgresDatetime(rawStr)) {
    const instant = explicitTz ? parseNaiveWallInZone(rawStr, tz) : parseNaiveUtcInstant(rawStr);
    if (instant) {
      try {
        return formatIntlInZone(instant, tz, intlOpts);
      } catch (_) {
        /* invalid IANA — fall through */
      }
    }
  }

  const m = parseToMoment(value);
  if (m) {
    try {
      return formatIntlInZone(m.toDate(), tz, intlOpts);
    } catch (_) {
      /* invalid IANA — fall through */
    }
  }

  return formatLocalDateTime(value, { withSeconds });
}

export function formatLocalDateTime(value, { withSeconds = true } = {}) {
  const m = parseToMoment(value);
  if (!m) return '';

  return m.local().format(withSeconds ? 'MM/DD/YYYY HH:mm:ss' : 'MM/DD/YYYY HH:mm');
}

